import { CommonModule } from '@angular/common';
import { Component, effect, inject, Input } from '@angular/core';
import { LocaleService } from 'src/app/services/locale';
import { UserService } from 'src/app/services/user';

@Component({
  selector: 'app-plan',
  imports: [CommonModule],
  template: `
    <div
      class="group flex flex-col h-full bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl hover:border-[#043B22]/20"
      [ngClass]="{
        'ring-2 ring-[#043B22] ring-offset-2': selected?.id === plan.id,
      }"
    >
      <!-- Plan Image -->
      <div class="relative h-36 sm:h-44 overflow-hidden">
        <img
          src="/images/welcome.png"
          class="object-cover h-full w-full transition-transform duration-500 group-hover:scale-105"
        />
        <div
          class="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"
        ></div>
        <!-- Plan Badge -->
        <div class="absolute bottom-3 left-3 right-3">
          <span
            class="inline-block px-3 py-1 bg-white/90 backdrop-blur-sm text-[#043B22] text-xs sm:text-sm font-medium rounded-full"
          >
            {{ plan.name }}
          </span>
        </div>
      </div>

      <!-- Plan Content -->
      <div class="flex-1 flex flex-col p-4 sm:p-5">
        <!-- Price -->
        <div class="text-center mb-4">
          <div class="flex items-baseline justify-center gap-1">
            <span
              class="text-2xl sm:text-3xl font-bold text-[#043B22] futura"
              >{{ plan.price }}</span
            >
            <span class="text-sm text-[#616161]">/ {{ plan.duration }}</span>
          </div>
          <span class="text-xs text-[#616161]">{{
            language === 'english' ? '+ VAT' : '+ IVA'
          }}</span>
        </div>

        <!-- Divider -->
        <div class="w-12 h-0.5 bg-[#043B22]/20 mx-auto mb-4"></div>

        <!-- Features -->
        <ul class="flex-1 space-y-2 sm:space-y-3 mb-4 sm:mb-6">
          <li
            *ngFor="let detail of plan.details"
            class="flex items-start gap-2 text-sm text-[#616161]"
          >
            <svg
              class="w-4 h-4 text-[#043B22] mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
            <span>{{ detail }}</span>
          </li>
        </ul>

        <!-- Selection Button (non-profile view) -->
        <div
          *ngIf="!profile"
          (click)="onSelect(plan)"
          class="w-full flex items-center justify-center gap-2 cursor-pointer"
        >
          <div
            class="w-5 h-5 rounded-full border-2 border-[#043B22] flex items-center justify-center transition-all"
            [ngClass]="{ 'bg-[#043B22]': selected?.id === plan.id }"
          >
            <div
              *ngIf="selected?.id === plan.id"
              class="w-2 h-2 bg-white rounded-full"
            ></div>
          </div>
          <span class="text-sm text-[#043B22] futura">
            {{
              selected?.id === plan.id
                ? language === 'english'
                  ? 'Selected'
                  : 'Selezionato'
                : language === 'english'
                  ? 'Select Plan'
                  : 'Seleziona Piano'
            }}
          </span>
        </div>

        <!-- Subscribe Button (profile view) -->
        <div *ngIf="profile && selected?.id !== plan.id" class="mt-auto">
          <button
            (click)="onSelect(plan)"
            [disabled]="!currentUser?.id"
            [ngClass]="{
              'opacity-50 cursor-not-allowed': !currentUser?.id,
              'hover:bg-[#043B22] hover:text-white hover:shadow-lg hover:shadow-[#043B22]/30':
                currentUser?.id,
            }"
            class="w-full px-4 py-2.5 sm:py-3 border-2 border-[#043B22] text-[#043B22] futura text-sm sm:text-base font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            {{ locale.common.subscribe }}
          </button>
          <p
            *ngIf="!currentUser?.id"
            class="text-red-500 text-xs text-center mt-2"
          >
            {{
              language === 'english'
                ? 'Complete your profile first'
                : 'Completa prima il tuo profilo'
            }}
          </p>
        </div>

        <!-- Currently Selected Badge (profile view) -->
        <div
          *ngIf="profile && selected?.id === plan.id"
          class="mt-auto px-4 py-2.5 sm:py-3 bg-[#043B22] text-white futura text-sm sm:text-base font-medium rounded-xl flex items-center justify-center gap-2"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
          {{ language === 'english' ? 'Current Plan' : 'Piano Attuale' }}
        </div>
      </div>
    </div>
  `,
})
export class PlanComponent {
  language = inject(LocaleService).getLanguage();
  private userService = inject(UserService);
  @Input() profile: any;
  @Input() plan: any;
  @Input() select: any;
  @Input() selected: any;

  localeService = inject(LocaleService);
  locale = this.localeService.locale();
  currentUser: any;
  constructor() {
    effect(() => {
      this.locale = this.localeService.locale();
      this.currentUser = this.userService.user();
    });
  }

  onSelect(plan: any) {
    if (!this.currentUser?.id && this.profile) {
      return;
    }
    this.select(plan);
  }
}
